import apiClient from './apiClient';

/**
 * Cache für Zählerarten (Stammdaten / Lookup-Tabelle) mit sehr langer TTL.
 * Zählerarten ändern sich nur durch Flyway-Migrationen, deshalb 24h TTL.
 *
 * Pattern analog zu propertyCacheService.js:
 * - In-Memory Cache + localStorage-Persistenz (überlebt Page-Reload)
 * - Request-Deduplizierung (Cache-Stampede-Schutz)
 * - ETag-Support (304 Not Modified)
 * - cacheInvalidationToken-Race-Condition-Schutz
 * - AbortController-Support via wrapSharedPromiseForCaller
 */
// v2: neue Seed-Art HEIZKOSTENVERTEILER (Migration V240) — Version bumpen, sonst sehen
// Bestandsclients die neue Art wegen der 24h-TTL bis zu 24h lang nicht.
const CACHE_VERSION = 'v2';
const CACHE_PREFIX = `zaehler-arten:${CACHE_VERSION}:`;
const LEGACY_CACHE_PREFIXES = ['zaehler-arten:v1:'];
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 Stunden (echte Stammdaten)

const memoryCache = new Map();
const pendingRequests = new Map();

let cacheInvalidationToken = 0;

const isLocalStorageAvailable = () => {
  try {
    const testKey = '__zaehler_arten_storage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

const storageAvailable = isLocalStorageAvailable();

/**
 * Räumt beim Laden des Moduls verwaiste localStorage-Einträge einer älteren
 * Cache-Version auf (Best-Effort), analog zaehlerCacheService.js. Sonst bleiben
 * alte v1-Keys (vor der neuen Seed-Art HEIZKOSTENVERTEILER) dauerhaft im
 * localStorage liegen.
 */
const cleanupLegacyCacheVersions = () => {
  if (!storageAvailable) return;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && LEGACY_CACHE_PREFIXES.some(prefix => key.startsWith(prefix))) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Ignorieren
  }
};
cleanupLegacyCacheVersions();

const isFresh = (entry) => {
  return !!entry && Date.now() - entry.timestamp < CACHE_TTL_MS;
};

const getFromLocalStorage = (key) => {
  if (!storageAvailable) return null;
  try {
    const item = localStorage.getItem(CACHE_PREFIX + key);
    if (!item) return null;
    const parsed = JSON.parse(item);
    if (!parsed || typeof parsed.timestamp !== 'number') return null;
    return parsed;
  } catch {
    return null;
  }
};

const setToLocalStorage = (key, value) => {
  if (!storageAvailable) return;
  try {
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(value));
  } catch {
    // Quota exceeded — Fallback auf Memory-Cache
  }
};

const removeFromLocalStorage = (key) => {
  if (!storageAvailable) return;
  try {
    localStorage.removeItem(CACHE_PREFIX + key);
  } catch {
    // Ignorieren bei Fehlern
  }
};

const invalidateCacheKey = (key) => {
  memoryCache.delete(key);
  pendingRequests.delete(key);
  removeFromLocalStorage(key);
};

const wrapSharedPromiseForCaller = (sharedPromise, signal) => {
  if (!signal) {
    return sharedPromise;
  }

  if (signal.aborted) {
    const abortError = new Error('Request aborted');
    abortError.name = 'AbortError';
    return Promise.reject(abortError);
  }

  return new Promise((resolve, reject) => {
    const handleAbort = () => {
      signal.removeEventListener('abort', handleAbort);
      const abortError = new Error('Request aborted');
      abortError.name = 'AbortError';
      reject(abortError);
    };

    signal.addEventListener('abort', handleAbort, { once: true });

    sharedPromise.then(
      (value) => {
        signal.removeEventListener('abort', handleAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener('abort', handleAbort);
        reject(error);
      }
    );
  });
};

const clearLocalStorageCache = () => {
  if (!storageAvailable) return;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Ignorieren bei Fehlern
  }
};

const getCached = (url, params, signal, _retryCount = 0) => {
  const cacheKey = params ? `${url}?${JSON.stringify(params)}` : url;

  // 1. Prüfe Memory-Cache (schnellster Pfad)
  const memoryCached = memoryCache.get(cacheKey);
  if (isFresh(memoryCached)) {
    return Promise.resolve(Array.isArray(memoryCached.data) ? [...memoryCached.data] : memoryCached.data);
  }
  if (memoryCached) {
    memoryCache.delete(cacheKey);
  }

  // 2. Prüfe localStorage-Cache
  const localCached = getFromLocalStorage(cacheKey);
  if (isFresh(localCached)) {
    memoryCache.set(cacheKey, localCached);
    return Promise.resolve(Array.isArray(localCached.data) ? [...localCached.data] : localCached.data);
  }
  if (localCached) {
    removeFromLocalStorage(cacheKey);
  }

  // 3. Deduplizierung: laufenden Request wiederverwenden
  if (pendingRequests.has(cacheKey)) {
    // Kein separater signal?.aborted-Guard nötig: wrapSharedPromiseForCaller prüft
    // signal.aborted selbst und rejected sofort, falls das Signal bereits gesetzt ist.
    return wrapSharedPromiseForCaller(
      pendingRequests.get(cacheKey).then(
        data => Array.isArray(data) ? [...data] : data
      ),
      signal
    );
  }

  // 4. API-Request mit ETag-Support
  const config = {
    validateStatus: (status) => status === 200 || status === 304
  };
  if (params) config.params = params;
  // Signal wird NICHT an axios weitergegeben: Ein StrictMode-Cleanup-Abort darf den
  // gemeinsamen HTTP-Request nicht abbrechen, während ein zweiter Caller bereits am
  // geteilten pendingRequests-Promise hängt. Die per-Caller-Isolation übernimmt
  // wrapSharedPromiseForCaller; der HTTP-Request läuft immer bis zum Abschluss durch.

  const staleEntry = localCached || memoryCached;
  const etag = staleEntry?.etag;
  if (etag) {
    config.headers = { 'If-None-Match': etag };
  }

  const currentToken = cacheInvalidationToken;

  const promise = apiClient.get(url, config).then(response => {
    // RACE-SCHUTZ: Nicht cachen wenn invalidateAll aufgerufen wurde
    if (currentToken !== cacheInvalidationToken) {
      if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
      if (response.status === 304) {
        const cachedData = staleEntry?.data;
        if (cachedData) return cachedData;
        if (_retryCount < 1) {
          return getCached(url, params, signal, _retryCount + 1);
        }
        return Array.isArray(response.data) ? [...response.data] : (response.data || []);
      }
      return Array.isArray(response.data) ? [...response.data] : response.data;
    }

    if (response.status === 304) {
      if (!staleEntry) {
        if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
        invalidateCacheKey(cacheKey);
        if (_retryCount < 1) {
          return getCached(url, params, signal, _retryCount + 1);
        }
        return [];
      }
      const refreshedEntry = {
        data: staleEntry.data,
        timestamp: Date.now(),
        etag: staleEntry.etag
      };
      memoryCache.set(cacheKey, refreshedEntry);
      setToLocalStorage(cacheKey, refreshedEntry);
      if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
      return staleEntry.data;
    }

    // 200 OK: Neue Daten cachen
    const data = response.data;
    const responseEtag = response.headers['etag'];
    const newEntry = {
      data,
      timestamp: Date.now(),
      etag: responseEtag
    };
    memoryCache.set(cacheKey, newEntry);
    setToLocalStorage(cacheKey, newEntry);
    if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
    return data;
  }).catch(err => {
    if (pendingRequests.get(cacheKey) === promise) pendingRequests.delete(cacheKey);
    throw err;
  });

  pendingRequests.set(cacheKey, promise);

  return wrapSharedPromiseForCaller(
    promise.then(data => Array.isArray(data) ? [...data] : data),
    signal
  );
};

const zaehlerArtCacheService = {
  /**
   * Lädt alle aktiven Zählerarten (gecacht, 24h TTL).
   * Singleton-Liste — keine Filter nötig (Stammdaten).
   * @param {object} [options]
   * @param {AbortSignal} [options.signal] - AbortController-Signal
   * @returns {Promise<Array>} Liste der ZaehlerArtDTOs
   */
  getZaehlerArten: ({ signal } = {}) => getCached('/zaehler-arten', undefined, signal),

  /**
   * Invalidiert alle Zählerart-Caches (z.B. nach Logout oder Admin-Aktion).
   */
  invalidateAll: () => {
    cacheInvalidationToken++;
    memoryCache.clear();
    pendingRequests.clear();
    clearLocalStorageCache();
  },

  /**
   * Löscht den kompletten Cache (bei Logout).
   */
  clearCache: () => {
    cacheInvalidationToken++;
    memoryCache.clear();
    pendingRequests.clear();
    clearLocalStorageCache();
  },

  /**
   * Für Unit-Tests: gibt interne Cache-Objekte zurück.
   * @returns {{ memoryCache: Map, pendingRequests: Map }}
   */
  _getInternals: () => ({ memoryCache, pendingRequests }),

  /**
   * Für Unit-Tests: setzt den Cache zurück.
   */
  _reset: () => {
    cacheInvalidationToken++;
    memoryCache.clear();
    pendingRequests.clear();
    clearLocalStorageCache();
  }
};

export default zaehlerArtCacheService;
